// apps/client/src/hooks/useTrailBookValuation.ts
import { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

interface UseTrailBookValuationParams {
  companyId: string;
  year: number;
  yearType: 'calendar' | 'financial';
  officeId?: string;
  entityCode?: string;
}

interface TemplateValuation {
  templateId: string;
  templateName: string | null;
  entityCode?: string | null;
  entityName?: string | null;
  trailIncomeAnnualisedCents: number;
}

interface UseTrailBookValuationReturn {
  templates: TemplateValuation[];
  loading: boolean;
  error: string | null;
  totalTrailIncome: number;
  monthsWithData: number;
}

/**
 * Hook to fetch trail book valuation by template
 * Used for: Trail Book Valuation reporting page
 *
 * Calculates:
 * - Annualized trail income per template
 * - Present value at 6x multiplier
 * - Company-wide totals
 */
export function useTrailBookValuation({
  companyId,
  year,
  yearType,
  officeId,
  entityCode,
}: UseTrailBookValuationParams): UseTrailBookValuationReturn {
  const [templates, setTemplates] = useState<TemplateValuation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [monthsWithData, setMonthsWithData] = useState(12);

  useEffect(() => {
    if (!companyId) {
      setLoading(false);
      return;
    }

    const fetchValuation = async () => {
      setLoading(true);
      setError(null);

      try {
        console.log(`[useTrailBookValuation] Calling BigQuery getTrailBookValuation:`, {
          companyId,
          year,
          yearType,
        });

        // Call BigQuery function
        const getTrailBookValuationFunction = httpsCallable(functions, 'getTrailBookValuation');
        const result = await getTrailBookValuationFunction({
          companyId,
          year,
          yearType,
          officeId,
          entityCode,
        });

        const data = result.data as {
          templates: TemplateValuation[];
          totals: {
            totalTrailIncomeCents: number;
          };
          monthsWithData: number;
        };

        console.log(`[useTrailBookValuation] BigQuery returned ${data.templates.length} templates, totals:`, {
          totalTrailIncome: data.totals.totalTrailIncomeCents / 100,
          monthsWithData: data.monthsWithData,
        });

        setTemplates(data.templates);
        setMonthsWithData(data.monthsWithData || 12);
      } catch (err: any) {
        console.error('[useTrailBookValuation] Error:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch trail book valuation');
        setTemplates([]);
      } finally {
        setLoading(false);
      }
    };

    fetchValuation();
  }, [companyId, year, yearType, officeId, entityCode]);

  // Calculate totals from templates
  const totalTrailIncome = templates.reduce((sum, t) => sum + t.trailIncomeAnnualisedCents, 0);

  return {
    templates,
    loading,
    error,
    totalTrailIncome,
    monthsWithData,
  };
}
